// The ledger reports of the Books (06 §6.6): general ledger, cash book, AR and AP ageing, the preview
// of the month-end FX revaluation and the year-end checklist. Returned by the callable `financeReport`
// as structured data (never a file); the office draws them and writes the CSV.
//
// Kept apart from finance.ts so that file's report union can include them without a cycle.
import { z } from 'zod';
import { sectorCodeSchema } from './config';

const signedMinor = z.number().int().min(-1e13).max(1e13);
const iso = z.string();
const period = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/);
const accountCode = z.string().regex(/^\d{4}$/);
const date = z.iso.date();

export const ledgerEntrySchema = z
  .object({
    journalId: z.string(),
    /** `KSA-J-2026-0000123`, once the ledger has numbered it. */
    journalRef: z.string().nullable(),
    businessDate: date,
    sourceType: z.string(),
    sourceId: z.string(),
    memo: z.string(),
    /** The line's own words (a manual journal's line description), else empty. */
    description: z.string(),
    debitMinor: signedMinor,
    creditMinor: signedMinor,
    /** Debit − credit after this line, from the opening balance. */
    balanceMinor: signedMinor,
  })
  .strict();
export type LedgerEntryRow = z.infer<typeof ledgerEntrySchema>;

/** Every line on one account between two dates, with the balance carried from before `from`. */
export const generalLedgerReportSchema = z
  .object({
    kind: z.literal('general_ledger'),
    entityId: sectorCodeSchema,
    currency: z.string(),
    accountCode,
    accountName: z.string(),
    /** The account's normal side: a credit account reads its balance as credit − debit. */
    normal: z.enum(['debit', 'credit']),
    from: date,
    to: date,
    openingMinor: signedMinor,
    entries: z.array(ledgerEntrySchema).max(5000),
    debitMinor: signedMinor,
    creditMinor: signedMinor,
    closingMinor: signedMinor,
    truncated: z.boolean(),
    generatedAt: iso,
  })
  .strict();
export type GeneralLedgerReport = z.infer<typeof generalLedgerReportSchema>;

/** One wallet (till, safe, bank, card machine, driver pouch): opening, every movement, closing. */
export const cashBookReportSchema = z
  .object({
    kind: z.literal('cash_book'),
    entityId: sectorCodeSchema,
    currency: z.string(),
    walletId: z.string(),
    walletName: z.string(),
    walletType: z.string(),
    accountCode,
    from: date,
    to: date,
    openingMinor: signedMinor,
    entries: z.array(ledgerEntrySchema).max(5000),
    inMinor: signedMinor,
    outMinor: signedMinor,
    closingMinor: signedMinor,
    /** Journals of the wallet the ledger has not applied yet: the balance may still move. */
    pendingJournals: z.number().int().nonnegative(),
    truncated: z.boolean(),
    generatedAt: iso,
  })
  .strict();
export type CashBookReport = z.infer<typeof cashBookReportSchema>;

export const ageingBucketsSchema = z
  .object({
    d0_30: signedMinor,
    d31_60: signedMinor,
    d61_90: signedMinor,
    d90_plus: signedMinor,
  })
  .strict();

export const ageingSectionSchema = z
  .object({
    key: z.enum(['customers', 'partners', 'suppliers']),
    title: z.string(),
    accountCode,
    parties: z
      .array(
        z
          .object({
            partyKey: z.string(),
            name: z.string(),
            totalMinor: signedMinor,
            buckets: ageingBucketsSchema,
            openItems: z.number().int().nonnegative(),
            oldestDate: date.nullable(),
          })
          .strict(),
      )
      .max(2000),
    totalMinor: signedMinor,
    buckets: ageingBucketsSchema,
    /** The account's balance on the date from the journals read; equals `totalMinor` when complete. */
    ledgerBalanceMinor: signedMinor,
  })
  .strict();
export type AgeingSection = z.infer<typeof ageingSectionSchema>;

/** Receivables (customers on 1100, partners holding our cash on 1003) or payables (suppliers on 2000). */
const ageingReportBase = z
  .object({
    entityId: sectorCodeSchema,
    currency: z.string(),
    asOf: date,
    sections: z.array(ageingSectionSchema),
    truncated: z.boolean(),
    generatedAt: iso,
  })
  .strict();
export const arAgeingReportSchema = ageingReportBase.extend({ kind: z.literal('ar_ageing') });
export const apAgeingReportSchema = ageingReportBase.extend({ kind: z.literal('ap_ageing') });
export type AgeingReport =
  z.infer<typeof arAgeingReportSchema> | z.infer<typeof apAgeingReportSchema>;

export const revaluationRowSchema = z
  .object({
    counterEntityId: sectorCodeSchema,
    currency: z.string(),
    /** `payable`: a loan from a country of the same kind, only the 2100 is restated (06 §6.3). */
    restate: z.enum(['both', 'payable']).optional(),
    rate: z.string(),
    rateDate: date,
    bookReceivableMinor: signedMinor,
    bookPayableMinor: signedMinor,
    foreignReceivableMinor: signedMinor,
    foreignPayableMinor: signedMinor,
    targetReceivableMinor: signedMinor,
    targetPayableMinor: signedMinor,
    receivableAdjustMinor: signedMinor,
    payableAdjustMinor: signedMinor,
    gainMinor: signedMinor,
  })
  .strict();
export type RevaluationRowDoc = z.infer<typeof revaluationRowSchema>;

/** What `revalueFx` would post for the month, without posting it. */
export const fxRevaluationReportSchema = z
  .object({
    kind: z.literal('fx_revaluation'),
    entityId: sectorCodeSchema,
    currency: z.string(),
    period,
    rows: z.array(revaluationRowSchema),
    /** Unrealised gain (positive) or loss of this run. */
    gainMinor: signedMinor,
    /** Pairs that are not foreign currency for this country, or not revalued, and why. */
    notes: z.array(z.string()),
    missingRates: z.array(z.object({ from: z.string(), to: z.string(), date }).strict()),
    /** Runs already posted for the month. */
    runs: z.number().int().nonnegative(),
    truncated: z.boolean(),
    generatedAt: iso,
  })
  .strict();
export type FxRevaluationReport = z.infer<typeof fxRevaluationReportSchema>;

export const yearChecklistItemSchema = z
  .object({
    key: z.enum([
      'year_ended',
      'months_closed',
      'unapplied_journals',
      'trial_balance',
      'data_complete',
    ]),
    label: z.string(),
    status: z.enum(['pass', 'fail']),
    detail: z.string(),
    open: z.array(z.string()).max(50),
  })
  .strict();

/** Before a year is closed: the checks, and the closing journal it would post. */
export const yearChecklistReportSchema = z
  .object({
    kind: z.literal('year_checklist'),
    entityId: sectorCodeSchema,
    currency: z.string(),
    year: z.number().int(),
    status: z.enum(['open', 'closed']),
    items: z.array(yearChecklistItemSchema),
    ready: z.boolean(),
    /** The year's profit (positive) or loss that the close moves to 3200. */
    netResultMinor: signedMinor,
    lines: z.array(
      z
        .object({
          accountCode,
          name: z.string(),
          debitMinor: signedMinor,
          creditMinor: signedMinor,
        })
        .strict(),
    ),
    truncated: z.boolean(),
    generatedAt: iso,
  })
  .strict();
export type YearChecklistReport = z.infer<typeof yearChecklistReportSchema>;
